import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, Pin, PinOff, CheckCircle2, RefreshCw, Trash2 } from 'lucide-react';
import type { Mailbox } from '../../../shared/api/types';
import { Badge } from '../../../shared/ui/Badge';
import { CopyButton } from '../../../shared/ui/CopyButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../shared/ui/DropdownMenu';
import { isMailboxActive, mailboxStatusView } from '../lib';
import { useNow } from '../../../shared/lib/useNow';
import { useForwardingReady } from '../../forwarding/useForwardingReady';
import { useOnboardingAnchor } from '../../onboarding/useOnboardingAnchor';
import { useForwardingHidden } from '../useForwardingHidden';

export interface MailboxCardActions {
  onPin: (m: Mailbox) => void;
  onUnpin: (m: Mailbox) => void;
  onActivate: (m: Mailbox) => void;
  onToggleForwarding: (m: Mailbox) => void;
  onDelete: (m: Mailbox) => void;
}

export function MailboxCard({
  mailbox,
  actions,
  anchored = false,
}: {
  mailbox: Mailbox;
  actions: MailboxCardActions;
  /** Якорь онбординга ставит только одна карточка списка — иначе шаг указал бы на произвольную. */
  anchored?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cardAnchor = useOnboardingAnchor('mailbox.card.first');
  const menuAnchor = useOnboardingAnchor('mailbox.card.menu');
  const now = useNow();
  const forwardingReady = useForwardingReady();
  const messagesHidden = useForwardingHidden() && mailbox.forwardingEnabled;
  const status = mailboxStatusView(mailbox, now);
  const inactive = status.tone === 'inactive';
  const active = isMailboxActive(mailbox, now);

  // Письма переадресуемого ящика не показываются, поэтому счётчик непрочитанных вводил бы в заблуждение.
  const secondaryLine = messagesHidden
    ? t('mailbox.forwardedHint')
    : mailbox.unreadCount > 0
      ? t('mailbox.newCount', { count: mailbox.unreadCount })
      : t('mailbox.noNew');

  return (
    <div
      className="flex items-center gap-3 rounded-2xl bg-surface p-4 shadow-card"
      {...(anchored ? cardAnchor : {})}
    >
      <button
        type="button"
        onClick={() => void navigate({ to: '/mailbox/$mailboxId', params: { mailboxId: mailbox.id } })}
        className="min-w-0 flex-1 text-left"
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className={`truncate font-semibold ${inactive ? 'text-fg-muted' : 'text-fg'}`}>
            {mailbox.address.full}
          </span>
          <Badge tone={status.tone === 'active' ? 'active' : 'inactive'}>
            {t(status.key, { time: status.time })}
          </Badge>
          {mailbox.forwardingEnabled && (
            <Badge tone="forwarding" dot>
              {t('status.forwarding')}
            </Badge>
          )}
        </div>
        <div className="mt-1 truncate text-sm text-fg-muted">
          {secondaryLine}
          {mailbox.description ? ` · ${mailbox.description}` : ''}
        </div>
      </button>

      <CopyButton value={mailbox.address.full} />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Меню ящика"
            className="grid h-9 w-9 place-items-center rounded-lg text-fg-muted hover:bg-page hover:text-fg"
            {...(anchored ? menuAnchor : {})}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {mailbox.pinned ? (
            <DropdownMenuItem
              icon={<PinOff />}
              onSelect={() => {
                actions.onUnpin(mailbox);
              }}
            >
              {t('mailbox.menu.unpin')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              icon={<Pin />}
              onSelect={() => {
                actions.onPin(mailbox);
              }}
            >
              {t('mailbox.menu.pin')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            icon={<CheckCircle2 />}
            disabled={active}
            onSelect={() => {
              actions.onActivate(mailbox);
            }}
          >
            {t('mailbox.menu.activate')}
          </DropdownMenuItem>
          {/* Без подтверждённой основной почты пересылать некуда — ведём в настройки (правки РТК). */}
          {forwardingReady ? (
            <DropdownMenuItem
              icon={<RefreshCw />}
              onSelect={() => {
                actions.onToggleForwarding(mailbox);
              }}
            >
              {mailbox.forwardingEnabled
                ? t('mailbox.menu.disableForwarding')
                : t('mailbox.menu.enableForwarding')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem icon={<RefreshCw />} onSelect={() => void navigate({ to: '/forwarding' })}>
              {t('mailbox.menu.setupForwarding')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            destructive
            icon={<Trash2 />}
            onSelect={() => {
              actions.onDelete(mailbox);
            }}
          >
            {t('mailbox.menu.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
